import React, { useState, useCallback, useMemo } from 'react';

import {
  FiltersCollection,
  ScreenAllFilters,
  UnAppliedChanges,
} from 'components/Filters';
import { ScreenPresets } from 'components/Presets';
import { withScreen } from 'components/Screen/withScreen';

import { CollapsibleBlock } from '@gogame/components';
import { Screen, createScreenRoute, useScreenDispatch } from '@gogame/screens';

import { compareSchema } from 'scenes/Trackers/Tracker/schemas';

import AllFilters from './components/Filters/AllFilters';
import ApplyButton from './components/ApplyButton';
import PrimaryFilters from './components/Filters/PrimaryFilters';
import TrackerCharts from './components/TrackerCharts';
import TrackerDataTable from './components/TrackerDataTable';
import { FILTER_APPLICATIONS, SCREEN_NAME } from './constants';
import { permission } from './constants';
import { atQuery } from './data/queryKeyFactory';
import { trackerLink, trackerSearchParams } from './navigation';
import { SET_FILTERS, SET_ADVANCED_FILTERS } from './reducers';
import { trackerTemporaryStateReducer, ensureTemporaryState } from './reducers';
import { filtersSchema, mapStateToPreset, mapPresetToState } from './schemas';
import { loadApplications } from './sources/filters';

const id = 2;

export { mapStateToPreset, mapPresetToState };

const TrackerScreen = withScreen({
  name: SCREEN_NAME,
  reducer: trackerTemporaryStateReducer,
  businessKeyQuery: atQuery,
  searchParams: trackerSearchParams,
})(({ screenState }) => {
  const [payToLocked, setPayToLocked] = useState(false);
  const [presetName, setPresetName] = useState('');
  const dispatch = useScreenDispatch();

  const handleLockPayTo = useCallback(() => {
    setPayToLocked((state) => !state);
  }, []);

  const handleSetCollection = (filtersCollection) =>
    dispatch({
      type: SET_FILTERS,
      payload: [...filtersCollection],
    });

  const handleCompare = (baseFilters) => [
    compareSchema.cast(baseFilters, {
      stripUnknown: true,
      context: {},
    }),
    baseFilters,
  ];
  const filtersCollection = useMemo(() => {
    const { filtersCollection } = ensureTemporaryState(screenState);
    return filtersCollection;
  }, [screenState]);

  const emptyValues = useMemo(() => {
    return [
      {
        ...filtersSchema.default(),
      },
    ];
  }, []);

  const handleChangeAdvancedFilters = (value) => {
    dispatch({
      type: SET_ADVANCED_FILTERS,
      payload: value,
    });

    window.dataLayer.push({
      event: 'applyAdvancedFilters',
      advancedFilters: value,
    });
  };
  const isCohortsMode = useMemo(
    () => !!screenState.cohortsSettings.metric,
    [screenState.cohortsSettings]
  );

  const temporaryAdvancedFilters = screenState.temporary?.advancedFilters;
  const advancedFilters = screenState.advancedFilters;

  const initialAdvancedFilters = temporaryAdvancedFilters || advancedFilters;

  const hasUnappliedAdvancedFilters =
    temporaryAdvancedFilters && temporaryAdvancedFilters !== advancedFilters;
  return (
    <React.Fragment>
      <Screen.Toolbar />
      {screenState.temporary ? <UnAppliedChanges /> : null}
      <Screen.Body>
        <ScreenPresets
          mapStateToPreset={mapStateToPreset}
          isAddPresetWithGroup
          isManageGroups
          grouped
          getActivePresetData={(preset) => {
            if (preset?.title) {
              setPresetName(preset.title);
            }
          }}
        />
        <FiltersCollection
          screen={SCREEN_NAME}
          controlled={true}
          initialValues={filtersCollection}
          emptyValues={emptyValues}
          onChange={handleSetCollection}
          onCompare={handleCompare}
          initialDirty={true}
        >
          <PrimaryFilters
            payToLocked={payToLocked}
            onLockPayTo={handleLockPayTo}
          />
          <ScreenAllFilters
            sidePage={AllFilters}
            badge={!!initialAdvancedFilters?.length}
            badgeColor={hasUnappliedAdvancedFilters ? 'warning' : 'error'}
            params={{
              payToLocked,
              onLockPayTo: handleLockPayTo,
              onChangeAdvancedFilters: handleChangeAdvancedFilters,
              initialAdvancedFilters: initialAdvancedFilters,
              hasUnappliedAdvancedFilters: hasUnappliedAdvancedFilters,

              isCohortsMode,
            }}
          />
          <Screen.Toolbar.FiltersApply>
            <ApplyButton />
          </Screen.Toolbar.FiltersApply>
        </FiltersCollection>
        <CollapsibleBlock showLabel={'Show charts'} hideLabel={'Hide charts'}>
          <TrackerCharts presetName={presetName} />
        </CollapsibleBlock>
        <TrackerDataTable
          presetName={presetName}
          isCohortsMode={isCohortsMode}
        />
      </Screen.Body>
    </React.Fragment>
  );
});

export default createScreenRoute({
  title: 'Tracker',
  link: trackerLink,
  screen: <TrackerScreen />,
  redesign: true,
  id: id,
  permission: permission,
  loader: async ({ request }) => {
    const url = new URL(request.url);
    const params = new URLSearchParams(url.search);
    const ready = params.get('ready');
    const applications = params.get(FILTER_APPLICATIONS);
    if (ready !== 'true' && !applications) {
      try {
        return await loadApplications({ isOnlyDefault: true });
      } catch (e) {}
    }
    return null;
  },
  mergeScreenStateWithLoadData: (initScreenState, data) => {
    const collection = [];
    const ids = data?.map(({ id }) => id);
    if (ids?.length && initScreenState.filtersCollection) {
      initScreenState.filtersCollection.forEach((filters) => {
        if (!filters[FILTER_APPLICATIONS]) {
          collection.push({ ...filters, [FILTER_APPLICATIONS]: ids });
        } else {
          collection.push(filters);
        }
      });
      return { ...initScreenState, filtersCollection: collection };
    } else {
      return initScreenState;
    }
  },

  knowledgeBaseUrl:
    'https://go-game.zendesk.com/hc/en-us/sections/360002560834-Tracker',
});
